/**
 * providers.js — free machine-translation providers for ANY language pair.
 *
 * Domain-agnostic: no app strings, no Firebase. Used by the i18n engine to fill the
 * long tail of never-seen labels. Providers are tried in order and each is isolated,
 * so adding/replacing one (e.g. a paid, higher-quality service) is a one-line change.
 *
 * Providers (all free, no API key):
 *   1. Google `gtx` endpoint — best quality; for Indian languages gives the
 *      transliteration users expect (tandoori naan → తందూరీ నాన్).
 *   2. MyMemory — fallback if Google is blocked/unreachable.
 * If BOTH fail, the caller shows the source text as-typed (never blank/broken).
 */

const clean = s => String(s || '').trim();
const TIMEOUT_MS = 6000;   // never let a flaky network hang a request

// fetch → JSON with a hard timeout. Throws on non-2xx/timeout so the caller tries the next provider.
async function fetchJson(url) {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await fetch(url, { signal: ctrl.signal });
    if (!res.ok) throw new Error(`http ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
  }
}

// Google's free `client=gtx` endpoint. Response: [[["<to>","<from>",…],…],…] → join segs.
async function googleProvider(text, from, to) {
  const data = await fetchJson(`https://translate.googleapis.com/translate_a/single?client=gtx&sl=${from}&tl=${to}&dt=t&q=${encodeURIComponent(text)}`);
  if (!Array.isArray(data) || !Array.isArray(data[0])) return null;
  return data[0].map(seg => (seg && seg[0]) || '').join('').trim() || null;
}

// MyMemory: free, no key/signup, CORS-enabled. ~500 bytes/query, daily word cap.
async function myMemoryProvider(text, from, to) {
  const json = await fetchJson(`https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=${from}|${to}`);
  const out = json && json.responseData && json.responseData.translatedText;
  if (!out || (json.responseStatus && Number(json.responseStatus) !== 200)) return null;
  if (/MYMEMORY WARNING|QUERY LENGTH LIMIT|INVALID|NO QUERY SPECIFIED/i.test(out)) return null;
  return out;
}

const DEFAULT_PROVIDERS = [googleProvider, myMemoryProvider];

/**
 * Translate one string `from` → `to`. Returns the translation, or null if every
 * provider failed OR only echoed the input (couldn't translate) — null means
 * "show the source as-typed". Same-language requests short-circuit to null.
 */
export async function translateText(text, from, to, providers = DEFAULT_PROVIDERS) {
  const src = clean(text);
  if (!src || from === to) return null;
  for (const provider of providers) {
    try {
      const out = await provider(src, from, to);
      const t = out && out.trim();
      if (t && t.toLowerCase() !== src.toLowerCase()) return t;
    } catch {
      /* provider unreachable → try the next */
    }
  }
  return null;
}

/**
 * Translate many strings `from` → `to` with bounded concurrency. De-dupes
 * case-insensitively. Returns Map(originalText → translatedText) for successes.
 */
export async function translateMany(texts, from, to, { concurrency = 3, providers } = {}) {
  const seen = new Set();
  const uniq = [];
  (texts || []).forEach((n) => {
    const c = clean(n);
    if (!c) return;
    const k = c.toLowerCase();
    if (seen.has(k)) return;
    seen.add(k);
    uniq.push(c);
  });
  const out = new Map();
  let i = 0;
  const worker = async () => {
    while (i < uniq.length) {
      const text = uniq[i++];
      const t = await translateText(text, from, to, providers);
      if (t) out.set(text, t);
    }
  };
  await Promise.all(Array.from({ length: Math.min(concurrency, uniq.length || 1) }, worker));
  return out;
}
